import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth, isCEO } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { formatApiError, fmtNum } from "@/lib/utils";
import { Trash2, Undo2, AlertTriangle, Search } from "lucide-react";

const KINDS = [
  { key: "batches", label: "Batches" },
  { key: "dispatches", label: "Dispatches" },
  { key: "transfers", label: "Transfers" },
  { key: "users", label: "Users" },
];

function daysAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function daysLeft(iso) {
  if (!iso) return 60;
  const d = new Date(iso);
  return Math.max(0, 60 - Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
}

function describe(kind, item) {
  if (kind === "batches") return `${item.batch_number} (${item.bags_count} bags @ ${item.location || "—"})`;
  if (kind === "dispatches") return `${(item.kind || "domestic").toUpperCase()} → ${item.customer} (${item.total_bags} bags)`;
  if (kind === "transfers") return `${item.from_location} → ${item.to_location} (${item.total_bags} bags)`;
  if (kind === "users") return `${item.name} <${item.email}> (${item.role})`;
  return item.id;
}

export default function Trash() {
  const { user } = useAuth();
  const canAct = isCEO(user); // recover / purge restricted
  const [data, setData] = useState({ batches: [], dispatches: [], transfers: [], users: [] });
  const [tab, setTab] = useState("batches");
  const [search, setSearch] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);
  const [purgeConfirm, setPurgeConfirm] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/trash");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => Object.fromEntries(KINDS.map((k) => [k.key, (data[k.key] || []).length])), [data]);

  const items = useMemo(() => {
    const list = data[tab] || [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((i) => JSON.stringify(i).toLowerCase().includes(term));
  }, [data, tab, search]);

  const recover = async (kind, id) => {
    setErr(""); setBusy(`r-${id}`);
    try { await api.post(`/trash/${kind}/${id}/recover`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const purge = async (kind, id) => {
    setErr(""); setBusy(`p-${id}`);
    try { await api.delete(`/trash/${kind}/${id}`); setPurgeConfirm(null); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        overline="ADMIN"
        title="Recently Deleted"
        description="Items deleted in the last 60 days. CEO can recover or permanently delete; after 60 days items are auto-purged."
        actions={
          <div className="flex items-center gap-1" style={{ minWidth: 220 }}>
            <Search size={14} color="var(--text-muted)" />
            <input
              type="text"
              placeholder={`Search ${tab}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: "6px 10px", fontSize: 13 }}
              data-testid="trash-search"
            />
          </div>
        }
      />

      {err && (
        <div className="card p-3 mb-4 text-xs" style={{ borderColor: "var(--red)", color: "var(--red)", background: "rgba(239,68,68,0.08)" }} data-testid="trash-error">
          {err}
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }}>
        {KINDS.map((k) => (
          <button
            key={k.key}
            className="px-4 py-2 text-sm transition-colors whitespace-nowrap"
            style={{
              color: tab === k.key ? "var(--text)" : "var(--text-muted)",
              borderBottom: tab === k.key ? "2px solid var(--brand-blue)" : "2px solid transparent",
              marginBottom: "-1px",
              fontWeight: tab === k.key ? 600 : 400,
            }}
            onClick={() => setTab(k.key)}
            data-testid={`trash-tab-${k.key}`}
          >
            {k.label} <span className="font-mono text-xs" style={{ color: "var(--text-dim)" }}>({counts[k.key] || 0})</span>
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Deleted</th>
                <th>By</th>
                <th>Time left</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="5" className="text-center py-8" style={{ color: "var(--text-muted)" }}>
                  {(data[tab] || []).length === 0 ? `No deleted ${tab} in the last 60 days.` : "No items match your search."}
                </td></tr>
              )}
              {items.map((i) => (
                <tr key={i.id} data-testid={`trash-row-${tab}-${i.id}`}>
                  <td>
                    <div className="font-mono text-xs">{describe(tab, i)}</div>
                    <div className="text-[10px]" style={{ color: "var(--text-dim)" }}>id: {i.id.slice(0, 8)}…</div>
                  </td>
                  <td className="text-xs">{daysAgo(i.deleted_at)}</td>
                  <td className="text-xs" style={{ color: "var(--text-muted)" }}>{i.deleted_by_name || "—"}</td>
                  <td>
                    <span className="font-mono text-xs" style={{ color: daysLeft(i.deleted_at) < 7 ? "var(--red)" : "var(--text-muted)" }}>
                      {daysLeft(i.deleted_at)}d
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2 justify-end">
                      {canAct ? (
                        <>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            onClick={() => recover(tab, i.id)}
                            disabled={busy === `r-${i.id}`}
                            data-testid={`recover-${i.id}`}
                          >
                            <Undo2 size={12} /> Recover
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            onClick={() => setPurgeConfirm({ kind: tab, item: i })}
                            data-testid={`purge-${i.id}`}
                          >
                            <Trash2 size={12} /> Delete permanently
                          </button>
                        </>
                      ) : (
                        <span className="text-xs" style={{ color: "var(--text-dim)" }}>view only</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {purgeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(13,42,78,0.45)" }} onClick={() => setPurgeConfirm(null)}>
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="purge-confirm-modal">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle size={24} color="var(--red)" />
              <h3 className="font-heading text-lg font-bold">Permanently delete?</h3>
            </div>
            <div className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
              You're about to permanently delete <strong>{describe(purgeConfirm.kind, purgeConfirm.item)}</strong>.
              This cannot be undone.
            </div>
            <div className="flex gap-3">
              <button className="btn btn-secondary flex-1" onClick={() => setPurgeConfirm(null)} data-testid="cancel-purge">Cancel</button>
              <button
                className="btn btn-danger flex-1"
                onClick={() => purge(purgeConfirm.kind, purgeConfirm.item.id)}
                disabled={busy === `p-${purgeConfirm.item.id}`}
                data-testid="confirm-purge"
              >
                <Trash2 size={14} /> Delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
